<template>
  <component
    :is="iconComponent"
    role="img"
    :class="iconClass"
    :width="iconWidth"
    :height="iconHeight"
    v-on="listeners"
  />
</template>

<script lang="ts">
import svgIconRegistry from './svg/index'
import { pascalCase } from '@/lib/change-case'

const iconRotates = [0, 90, 180, 270] as const

function iconName (name: unknown): string {
  const pascalCaseName = pascalCase(typeof name === 'string' ? name : '')
    .replace(/-/g, '')

  return `BSvg${pascalCaseName}`
}

export const iconNameValidator = (value: unknown): boolean => {
  return Object.prototype.hasOwnProperty.call(svgIconRegistry, iconName(value))
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import icons from './svg/index'

defineOptions({
  name: 'b-svg',
})

const emit = defineEmits<{
  click: []
}>()

const props = defineProps({
  name: {
    type: String,
    required: true,
    validator: iconNameValidator,
  },
  size: {
    type: [String, Number],
    default: 24,
  },
  width: {
    type: [String, Number],
  },
  height: {
    type: [String, Number],
  },
  color: {
    type: String,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  rotate: {
    type: String,
    validator: (value: unknown) =>
      typeof value === 'string' && (iconRotates as readonly number[]).includes(Number(value)),
  },
})

const listeners = computed(() => ({
  click: () => {
    emit('click')
  },
}))

const iconComponent = computed(() => icons[iconName(props.name) as keyof typeof icons])

const iconWidth = computed((): string | number => props.width || props.size)

const iconHeight = computed((): string | number => props.height || props.size)

const iconClass = computed(() => ({
  'b-svg': true,
  [`b-svg-name-${props.name}`]: true,
  'b-icon-inline': !!props.inline,
  [`b-text-${props.color}`]: !!props.color,
  [`b-icon-rotate-${props.rotate}`]: !!props.rotate,
}))
</script>
